import { DOCUMENT, inject, Injectable } from '@angular/core';

declare global {
  interface Window {
    grecaptcha: {
      execute: (siteKey: string, options: { action: string }) => Promise<string>;
      ready: (callback: () => void) => void;
    };
  }
}

@Injectable({
  providedIn: 'root',
})
export class RecaptchaService {
  private document = inject(DOCUMENT);
  private scriptLoaded = false;
  private badgeOriginalParent: HTMLElement | null = null;
  private badgeOriginalPosition: string | null = null;

  loadScript(siteKey: string, renderUrl: string): Promise<void> {
    return new Promise((resolve, reject) => {
      if (this.scriptLoaded || window.grecaptcha) {
        resolve();
        return;
      }

      const script = this.document.createElement('script');
      script.src = `${renderUrl}?render=${siteKey}`;
      script.async = true;
      script.defer = true;
      script.onload = () => {
        this.scriptLoaded = true;
        resolve();
      };
      script.onerror = (err) => reject(err);
      this.document.head.appendChild(script);
    });
  }
  
  async execute(siteKey: string, renderUrl: string, action: string): Promise<string> {
    await this.loadScript(siteKey, renderUrl);

    return new Promise((resolve) => {
       window.grecaptcha.ready(async () => {
         const token = await window.grecaptcha.execute(siteKey, { action });
         resolve(token);
       });
    });
  }

  private getBadge(): HTMLElement | null {
    return this.document.querySelector<HTMLElement>('.grecaptcha-badge');
  }

  /**
   * Skryje grecaptcha-badge, který si Google skript vytvoří v rootu stránky.
   * Pokud badge ještě není vykreslen (skript se načítá asynchronně), počká
   * přes MutationObserver na jeho vznik a skryje jej až poté.
   */
  hideBadge(): void {
    const badge = this.getBadge();
    if (badge) {
      badge.style.visibility = 'hidden';
      return;
    }

    const observer = new MutationObserver(() => {
      const b = this.getBadge();
      if (b) {
        b.style.visibility = 'hidden';
        observer.disconnect();
      }
    });
    observer.observe(this.document.body, { childList: true });
  }

  /**
   * Přesune grecaptcha-badge z rootu do zadaného elementu (modál zpětné vazby)
   * a zviditelní jej. Google badge má position: fixed (kotvený k oknu), přepneme
   * jej na position: absolute, aby se kotvil k rámečku modálu (ten má position:
   * relative).
   */
  showBadgeIn(target: HTMLElement): void {
    const badge = this.getBadge();
    if (!badge) return;

    if (!this.badgeOriginalParent) {
      this.badgeOriginalParent = badge.parentElement as HTMLElement;
    }
    if (this.badgeOriginalPosition === null) {
      this.badgeOriginalPosition = badge.style.position;
    }

    target.appendChild(badge);
    badge.style.position = 'absolute';
    badge.style.visibility = 'visible';
  }

  /**
   * Vrátí grecaptcha-badge zpět do rootu stránky, obnoví jeho původní position
   * (fixed) a opět jej skryje. Stav vysunutí (right) ponecháme na reCAPTCHA.
   */
  restoreBadge(): void {
    const badge = this.getBadge();
    if (!badge) return;

    if (this.badgeOriginalParent) {
      this.badgeOriginalParent.appendChild(badge);
    }
    badge.style.position = this.badgeOriginalPosition ?? 'fixed';
    badge.style.visibility = 'hidden';
  }
}
